import { Regulations } from '../../api/fishrules/getSpeciesRegulationsAtLocation';
import { ICoordinates } from '@app-types/geo';
import { InfoCard } from '../Cards/InfoCard/InfoCard';
import { Heading } from '../Heading/Heading';
import { WarningIcon } from '../Icons/WarningIcon';
import { Text } from '../Text/Text';

import { AggregateLimits } from './AggregateLimits';
import { getSeasonState } from './helpers';

interface IProps {
  regulation: Regulations;
  coordinates: ICoordinates;
  infoCardSize: 'lg' | 'sm';
  isProhibitedLocation: boolean;
  prohibitedMessage?: string;
}

export const RegulationCards = ({
  regulation,
  coordinates,
  infoCardSize,
  isProhibitedLocation,
  prohibitedMessage,
}: IProps) => {
  const isProhibited = isProhibitedLocation || regulation.prohibited === 1;
  const season = getSeasonState(regulation.seasons);

  if (isProhibited) {
    return (
      <div className="text-error-shade col-span-full mb-8">
        <div className="mb-2">
          <WarningIcon />
        </div>
        <Text variant="sm" className="mb-0 font-semibold">
          {prohibitedMessage ??
            'It is unlawful to harvest, possess, land, purchase, sell, or exchange this species.'}
        </Text>
      </div>
    );
  }

  return (
    <>
      <InfoCard
        size={infoCardSize}
        modalContent={season.component}
        iconText={season.seasonLabel}
        title="Seasons"
        bgColorClassname={season.seasonLabel === 'Open' ? 'bg-success-tint' : 'bg-error-tint'}
        headingColorClassName="text-trench"
        dataTestid="seasons-info-icon"
      />
      {regulation.bag_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Bag limit
              </Heading>
              <Text className="mb-0">
                The maximum number of individual fish legally harvested per harvester per day. Bag
                limits are only for properly licensed anglers actively harvesting the species.
                People who are not actively harvesting or are not properly licensed (if a license is
                required) may NOT be counted for the purpose of bag limits.
              </Text>
            </>
          }
          iconText={regulation.bag_limit.toString()}
          title="Bag limit"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.vessel_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Vessel limit
              </Heading>
              <Text className="mb-0">
                The maximum number of individual fish legally possessed aboard any vessel.
              </Text>
            </>
          }
          iconText={regulation.vessel_limit.toString()}
          title="Vessel limit"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.trophy_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Memorable / trophy limits
              </Heading>
              <Text className="mb-0">
                The maximum number of trophy individuals legally harvested per harvester per day. A
                trophy is any individual of a length greater than the max size limit. Trophy
                individuals are included in the daily bag limit unless stated otherwise.
              </Text>
            </>
          }
          iconText={`${regulation.trophy_limit} > ${regulation.trophy_size}`}
          title="Memorable"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.min_size !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Min size
              </Heading>
              <Text className="mb-0">
                No person shall harvest any individual fish less than the minimum size limit.
              </Text>
            </>
          }
          iconText={`${regulation.min_size}${regulation.measurement_unit_symbol}`}
          title="Min size"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.max_size !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Max size
              </Heading>
              <Text className="mb-0">
                No person shall harvest any individual fish greater than the maximum size except as
                permitted.
              </Text>
            </>
          }
          iconText={`${regulation.max_size}${regulation.measurement_unit_symbol}`}
          title="Max size"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.measurement_name && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                {`Measurement: ${regulation.measurement_name}`}
              </Heading>
              <Text className="mb-0">{regulation.measurement_description}</Text>
            </>
          }
          iconText={regulation.measurement_name}
          title="Measurement"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.gear_name && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                {regulation.gear_name}
              </Heading>
              <Text className="mb-0">{regulation.gear_description}</Text>
            </>
          }
          iconText=""
          title="Special gear"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.aggregate_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={<AggregateLimits coordinates={coordinates} regulation={regulation} />}
          iconText={regulation.aggregate_limit.toString()}
          title="Aggregate"
          bgColorClassname="bg-white"
        />
      )}
      {regulation.keep_intact === 1 && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Keep intact
              </Heading>
              <Text className="mb-0">
                In waters where minimum-length or slot-size limits apply, game fish may not be
                filleted, nor their head or tail fin removed until the angler has completed fishing
                for the day.
              </Text>
            </>
          }
          iconText="Keep intact"
          title="Keep intact"
          bgColorClassname="bg-white"
        />
      )}
    </>
  );
};
